import { Avatar, Button, Card, CardBody, Tooltip } from '@nextui-org/react'
import { AnimatePresence, motion, useDragControls } from 'framer-motion'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icons } from '@/components/icons'
import { MessageAnimation, MessageOptions } from '@/types/message'

type MessageType = 'success' | 'error' | 'warning' | 'info' | 'question'
type MessageSize = 'sm' | 'md' | 'lg'
type ButtonColor =
    | 'success'
    | 'danger'
    | 'warning'
    | 'primary'
    | 'secondary'
    | 'default'

interface MessageCardProps extends Omit<MessageOptions, 'animation'> {
    isVisible: boolean
    animation?: MessageAnimation
    size?: MessageSize
    onYes?: () => void
    onNo?: () => void
    onCancel?: () => void
    yesText?: string
    noText?: string
    cancelText?: string
    className?: string
}

const getIconByType = (type: MessageType): JSX.Element => {
    const icons = {
        success: <Icons.Check className="text-success text-xl" />,
        error: <Icons.Times className="text-danger text-xl" />,
        warning: <Icons.Exclamation className="text-warning text-xl" />,
        question: <Icons.Question className="text-primary text-xl" />,
        info: <Icons.Info className="text-secondary text-xl" />
    }

    return icons[type]
}
// Update getTypeColor to return ButtonColor type
const getTypeColor = (type: MessageType): ButtonColor => {
    const colors: Record<MessageType, ButtonColor> = {
        success: 'success',
        error: 'danger',
        warning: 'warning',
        question: 'secondary',
        info: 'primary'
    }

    return colors[type]
}

const sizeClasses: Record<MessageSize, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg'
}

// eslint-disable-next-line max-lines-per-function, complexity
export const MessageCard = ({
    isVisible,
    title,
    description,
    htmlBody,
    type = 'info',
    size = 'md',
    animation = 'scale',
    showYes,
    showNo,
    showCancel,
    className = '',
    yesText = 'Yes',
    noText = 'No',
    cancelText = 'Cancel',
    onYes,
    onNo,
    onCancel
}: MessageCardProps): JSX.Element => {
    const { t } = useTranslation()
    const dragControls = useDragControls()
    const [isDragging, setIsDragging] = useState(false)

    return (
        <AnimatePresence mode="wait">
            {isVisible && (
                <motion.div
                    drag
                    dragControls={dragControls}
                    dragMomentum={false}
                    onDragStart={() => setIsDragging(true)}
                    onDragEnd={() => setIsDragging(false)}
                    whileDrag={{ scale: 1.02 }}
                    initial={
                        animation === 'scale'
                            ? { scale: 0.9, opacity: 0 }
                            : { y: -100, opacity: 0 }
                    }
                    animate={{ scale: 1, y: 0, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ type: 'spring', bounce: 0.3 }}
                    className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full ${sizeClasses[size]}`}
                >
                    <Card
                        className={`
              relative overflow-hidden
              bg-white
              shadow-xl hover:shadow-2xl transition-shadow
              ${className}
            `}
                    >
                        <CardBody className="relative overflow-hidden p-6 pt-8">
                            {/* Drag Handle */}
                            <Tooltip
                                content={t('messageCard.tooltips.dragToMove')}
                            >
                                <div
                                    className="absolute top-1 left-1/2 transform -translate-x-1/2 p-1 cursor-move
                            rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                                    onPointerDown={(e) => dragControls.start(e)}
                                >
                                    <Icons.GripVertical className="text-gray-500" />
                                </div>
                            </Tooltip>

                            {/* Content */}
                            <div className="flex flex-col items-center text-center mt-2">
                                {/* Avatar */}
                                <Avatar
                                    icon={getIconByType(type)}
                                    className={`
    w-16 h-16 mb-4
    ring-2 ring-offset-2 ring-default-200
    bg-default-50
  `}
                                />

                                {/* Title */}
                                <motion.h2
                                    className="text-2xl font-bold mb-4 text-gray-800"
                                    initial={{ y: -20 }}
                                    animate={{ y: 0 }}
                                >
                                    {title || t(`messageCard.titles.${type}`)}
                                </motion.h2>

                                {/* Description */}
                                <motion.div
                                    className="mb-6 w-full"
                                    initial={{ y: -10, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.1 }}
                                >
                                    {htmlBody ? (
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: htmlBody
                                            }}
                                            className="prose max-w-none"
                                        />
                                    ) : (
                                        <p className="text-gray-600">
                                            {description ||
                                                t(
                                                    `messageCard.defaultMessages.${type}`
                                                )}
                                        </p>
                                    )}
                                </motion.div>

                                {/* Buttons */}
                                <motion.div
                                    className="flex justify-center gap-3 w-full"
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    {showCancel && (
                                        <Button
                                            color="default"
                                            variant="light"
                                            onPress={onCancel}
                                            className="min-w-[100px]"
                                            isDisabled={isDragging}
                                        >
                                            {cancelText}
                                        </Button>
                                    )}
                                    {showNo && (
                                        <Button
                                            color="danger"
                                            variant="flat"
                                            onPress={onNo}
                                            className="min-w-[100px]"
                                            isDisabled={isDragging}
                                        >
                                            {noText}
                                        </Button>
                                    )}
                                    {showYes && (
                                        <Button
                                            color={getTypeColor(type)}
                                            variant="solid"
                                            onPress={onYes}
                                            className="min-w-[100px]"
                                            isDisabled={isDragging}
                                        >
                                            {yesText}
                                        </Button>
                                    )}
                                </motion.div>
                            </div>

                            {/* Close button */}
                            {onCancel && (
                                <Tooltip
                                    content={t(
                                        'messageCard.tooltips.closeMessage'
                                    )}
                                >
                                    <Button
                                        isIconOnly
                                        size="sm"
                                        variant="light"
                                        onPress={onCancel}
                                        className="absolute top-2 right-2"
                                        isDisabled={isDragging}
                                    >
                                        <Icons.Times />
                                    </Button>
                                </Tooltip>
                            )}
                        </CardBody>
                    </Card>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
